import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_VIDEO = '/building-video.mp4';
const HERO_POSTER = '/building-poster.jpg';

export default function TowerSelectPage() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [towers, setTowers] = useState([]);

  useEffect(() => {
    axios.get(`${API}/towers`).then(r => setTowers(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="tower-select-page" className="relative min-h-screen bg-black overflow-hidden">
      {/* Background video */}
      <div className="fixed inset-0 z-0">
        <video
          autoPlay muted loop playsInline preload="auto"
          poster={HERO_POSTER}
          className="absolute w-full h-full object-cover opacity-40"
          data-testid="tower-bg-video"
        >
          <source src={`${HERO_VIDEO}?v=2`} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-24">
        {/* Overline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6"
          style={{ fontFamily: "'Outfit', sans-serif" }}
          data-testid="tower-overline"
        >
          {lang === 'es' ? 'CERRO DE LA PLATA PRESENTA' : 'CERRO DE LA PLATA PRESENTS'}
        </motion.p>

        {/* Main title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="text-5xl sm:text-6xl lg:text-8xl font-light tracking-[-0.02em] mb-4 gold-text text-center"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
          data-testid="tower-main-title"
        >
          Kirei
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-sm md:text-base text-white/50 font-light max-w-md text-center mb-16 leading-relaxed"
          style={{ fontFamily: "'Outfit', sans-serif" }}
          data-testid="tower-subtitle"
        >
          {lang === 'es'
            ? 'Selecciona una torre para explorar'
            : 'Select a tower to explore'}
        </motion.p>

        {/* Tower cards */}
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 w-full max-w-3xl">
          {towers.map((tower, idx) => {
            const stats = { available: 0, reserved: 0, sold: 0 };
            if (tower.floors_data) {
              // Count from apartments data
            }
            // Use the stats from the tower endpoint
            return (
              <motion.button
                key={tower.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1.0 + idx * 0.2 }}
                onClick={() => navigate(`/tower/${tower.id}`)}
                data-testid={`tower-card-${tower.id}`}
                className="flex-1 group relative border border-white/10 bg-black/40 backdrop-blur-xl p-8 sm:p-10 text-left transition-all duration-500 hover:border-[#D4AF37]/50 hover:bg-black/60"
              >
                <h2
                  className="text-3xl sm:text-4xl font-light tracking-tight mb-3 gold-text transition-all duration-300"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {tower.name}
                </h2>
                <div className="space-y-2 mb-8">
                  <div className="flex items-center gap-2">
                    <Building2 size={14} className="text-white/30" />
                    <span className="text-xs text-white/40 tracking-[0.08em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {tower.total_floors} {lang === 'es' ? 'pisos' : 'floors'} — {tower.total_units || 0} {lang === 'es' ? 'unidades' : 'units'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[#D4AF37] group-hover:gap-3 transition-all duration-300">
                  <span className="text-xs tracking-[0.12em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {lang === 'es' ? 'Explorar' : 'Explore'}
                  </span>
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </div>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none border border-[#D4AF37]/20" />
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
